import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BarChart3, TrendingUp, TrendingDown, Minus, AlertTriangle, Scale, ChevronRight } from 'lucide-react';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Party colors
const partyColors = {
  labour: { bg: 'bg-red-500', text: 'text-red-600', bar: 'bg-red-500' },
  conservative: { bg: 'bg-blue-500', text: 'text-blue-600', bar: 'bg-blue-500' },
  libdem: { bg: 'bg-orange-500', text: 'text-orange-500', bar: 'bg-orange-500' },
  snp: { bg: 'bg-yellow-500', text: 'text-yellow-600', bar: 'bg-yellow-500' },
  reform: { bg: 'bg-cyan-500', text: 'text-cyan-600', bar: 'bg-cyan-500' },
  green: { bg: 'bg-green-500', text: 'text-green-600', bar: 'bg-green-500' },
  plaid: { bg: 'bg-emerald-500', text: 'text-emerald-600', bar: 'bg-emerald-600' },
  dup: { bg: 'bg-red-800', text: 'text-red-800', bar: 'bg-red-800' },
  sinnfein: { bg: 'bg-green-800', text: 'text-green-800', bar: 'bg-green-800' }
};

export default function ComparePage() {
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchComparison();
  }, []);

  const fetchComparison = async () => {
    try {
      const response = await axios.get(`${API}/stats/comparison`);
      setComparison(response.data);
    } catch (error) {
      console.error('Failed to fetch comparison:', error);
    } finally {
      setLoading(false);
    }
  };

  const maxCount = comparison?.comparison?.[0]?.count || 1;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-20" data-testid="compare-page">
      <SEO title="Party Comparison" description="Compare UK political parties by documented scandals and accountability issues" />
      
      {/* Hero Header */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-white/10 rounded-xl">
              <Scale className="h-8 w-8" />
            </div>
            <div>
              <span className="text-xs text-slate-400 uppercase tracking-wider font-medium">
                Accountability Index
              </span>
              <h1 className="text-3xl md:text-4xl font-black">Party Comparison</h1>
            </div>
          </div>
          <p className="text-lg text-slate-300 max-w-2xl">
            See how each political party ranks in our scandal and misconduct database. 
            More articles indicate more documented accountability issues.
          </p>
        </div>
      </section>

      {/* Comparison Chart */}
      <section className="py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="space-y-6">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="flex items-center space-x-4">
                  <div className="skeleton h-6 w-32"></div>
                  <div className="skeleton h-10 flex-1"></div>
                  <div className="skeleton h-6 w-16"></div>
                </div>
              ))}
            </div>
          ) : comparison ? (
            <>
              {/* Total */}
              <div className="text-center mb-12 p-8 bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl shadow-xl">
                <p className="text-6xl font-black mb-2">{comparison.total_articles}</p>
                <p className="text-sm uppercase tracking-wider text-slate-400">
                  Total Documented Issues
                </p>
              </div>

              {/* Chart */}
              <div className="space-y-6">
                {comparison.comparison.map((party, index) => (
                  <Link 
                    to={`/search?party=${party.party}`}
                    key={party.party} 
                    className="block animate-fadeIn bg-white rounded-xl border border-slate-200 p-4 hover:shadow-lg hover:border-slate-300 transition-all cursor-pointer group"
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid={`party-bar-${party.party}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl font-black text-slate-300">
                          #{index + 1}
                        </span>
                        <span className={`font-bold ${partyColors[party.party]?.text || 'text-slate-600'}`}>
                          {party.label}
                        </span>
                        <ChevronRight className="h-4 w-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div className="flex items-center space-x-2 sm:space-x-4 text-sm">
                        {party.recent_count > 0 && (
                          <span className="hidden sm:flex items-center text-red-500 font-medium">
                            <TrendingUp className="h-4 w-4 mr-1" />
                            +{party.recent_count} this week
                          </span>
                        )}
                        <span className="font-black text-xl">
                          {party.count}
                        </span>
                        <span className="text-slate-400 hidden sm:inline">
                          ({party.percentage}%)
                        </span>
                      </div>
                    </div>
                    
                    {/* Bar */}
                    <div className="h-8 sm:h-10 bg-slate-100 overflow-hidden rounded">
                      <div 
                        className={`h-full ${partyColors[party.party]?.bar || 'bg-slate-500'} transition-all duration-1000 ease-out`}
                        style={{ width: `${(party.count / maxCount) * 100}%` }}
                      />
                    </div>
                  </Link>
                ))}
              </div>

              {/* Disclaimer */}
              <div className="mt-12 p-6 bg-yellow-50 border border-yellow-200">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-yellow-800 mb-1">Important Note</h3>
                    <p className="text-sm text-yellow-700">
                      These figures represent the number of documented issues in our database, 
                      sourced from news reports and user submissions. Higher numbers may reflect 
                      greater media coverage rather than more misconduct. Always verify 
                      information from original sources.
                    </p>
                  </div>
                </div>
              </div>

              {/* Last Updated */}
              <p className="text-center text-xs text-slate-400 mt-8 font-mono">
                Last updated: {new Date(comparison.generated_at).toLocaleString()}
              </p>
            </>
          ) : (
            <p className="text-center text-slate-500">Failed to load comparison data</p>
          )}
        </div>
      </section>
    </div>
  );
}
